import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import { Button } from 'antd';
import {
	plannedMaintenanceTemplatesRestCrudThunksForBuyer,
	plannedMaintenanceTemplatesRestCrudThunksForSupplier,
} from '../../thunks/planned_maintenance_templates_thunks';
import { EmptyState } from '../empty_state/EmptyState';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { PlannedMaintenanceTemplateRowDisplay } from '../planned_maintenance_template_row_display/PlannedMaintenanceTemplateRowDisplay';
import LogOnMountWithStandardEventProperties from '../log_on_mount_with_standard_event_properties/LogOnMountWithStandardEventProperties';
import { PLANNED_MAINTENANCE_TEMPLATES_CRUD_ACTION_CREATORS } from '../../actions/planned_maintenance_templates_actions';
import { ROLE_TYPES } from '../../utils/DataConstants';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import { useFilters } from '../../hooks/useFilters';
import { getPMScheduleTemplateFilterConfig } from '../../utils/PMScheduleTemplateFilterUtils';

require('./PlannedMaintenanceTemplatesIndexPage.less');

const PLANNED_MAINTENANCE_SCHEDULE_TEMPLATES_OVERVIEW_TARGET_COLLECTION =
	'plannedMaintenanceTemplatesIndex';
const PM_TEMPLATE_ADVANCED_FILTERS = 'plannedMaintenanceAdvancedFilters';

const PlannedMaintenanceTemplatesIndexPage: FC<any> = ({
	history,
	location,
	updateFilters,
	updateSorters,
	clearAndUpdateFilters,
	plannedMaintenanceTemplates,
	userType,
}): React.ReactElement => {
	const currentUser = useSelector((state: any) => state.session.currentUser);

	const columns = useMemo(
		() => [
			{
				title: 'Template Name',
				dataIndex: 'plannedMaintenanceTemplateId',
				render: (text, record) => (
					<PlannedMaintenanceTemplateRowDisplay plannedMaintenanceTemplate={record} />
				),
			},
		],
		[]
	);

	const onRow = useCallback(
		(record) => ({
			onClick: () => {
				let searchString = location.search;
				if (searchString[0] === '?') {
					searchString = searchString.slice(1);
				}
				let backUrl = `/${userType}/plannedMaintenance/templates/detail/${encodeURIComponent(
					record.id
				)}/details`;
				if (searchString && searchString.length > 0) {
					backUrl = backUrl.concat(`?backlinkParams=${encodeURIComponent(searchString)}`);
				}
				history.push(backUrl);
			},
		}),
		[history, location.search, userType]
	);

	const pmTemplateFilters = useFilters({
		filterTargetCollectionName: PM_TEMPLATE_ADVANCED_FILTERS,
		overviewCollectionName: PLANNED_MAINTENANCE_SCHEDULE_TEMPLATES_OVERVIEW_TARGET_COLLECTION,
		updateFilters: useCallback(
			(filters) =>
				updateFilters(filters, PLANNED_MAINTENANCE_SCHEDULE_TEMPLATES_OVERVIEW_TARGET_COLLECTION),
			[updateFilters]
		),
	});

	const filterConfig = useMemo(
		() => getPMScheduleTemplateFilterConfig({ filters: pmTemplateFilters }),
		[pmTemplateFilters]
	);

	return (
		<>
			<LogOnMountWithStandardEventProperties
				eventType={`visited ${userType} new pm templates index page`}
			/>
			<PaginatedReduxTableWithHeader
				targetCollectionName={PLANNED_MAINTENANCE_SCHEDULE_TEMPLATES_OVERVIEW_TARGET_COLLECTION}
				stateSlice={plannedMaintenanceTemplates}
				updateFilters={updateFilters}
				updateSorters={updateSorters}
				clearAndUpdateFilters={clearAndUpdateFilters}
				filterConfig={filterConfig}
				entityCollectionName="planned_maintenance_templates"
				tableColumns={columns}
				fetchData={
					userType === ROLE_TYPES.SUPPLIER
						? plannedMaintenanceTemplatesRestCrudThunksForSupplier.read
						: plannedMaintenanceTemplatesRestCrudThunksForBuyer.read
				}
				onTableRow={onRow}
				keyAccessor={(el) => el.id}
				initialPagination={{ current: 1, pageSize: 10 }}
				advancedFiltersInputParamName="title"
				rightActions={
					<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_PM}>
						<Button
							size="large"
							type="primary"
							onClick={() => history.push(`/${userType}/plannedMaintenance/templates/new`)}
						>
							Add Template
						</Button>
					</AccessPermissionChecker>
				}
				emptyState={
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
								alt=""
							/>
						}
						headline={"It's a little lonely in here."}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									Looks like your team has not created any planned maintenance templates yet.
								</div>
								<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_PM}>
									<Button
										type="primary"
										onClick={() => history.push(`/${userType}/plannedMaintenance/templates/new`)}
									>
										Add template
									</Button>
								</AccessPermissionChecker>
							</div>
						}
					/>
				}
			/>
		</>
	);
};

const mapStateToProps = (state) => ({
	plannedMaintenanceTemplates: state.planned_maintenance_templates,
});

const mapDispatchToProps = (dispatch) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(
			PLANNED_MAINTENANCE_TEMPLATES_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)
		),
	updateSorters: (sortBy, order, targetCollection) =>
		dispatch(
			PLANNED_MAINTENANCE_TEMPLATES_CRUD_ACTION_CREATORS.updateSorting(
				sortBy,
				order,
				targetCollection
			)
		),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			PLANNED_MAINTENANCE_TEMPLATES_CRUD_ACTION_CREATORS.clearAndUpdateFilters(
				filters,
				targetCollectionName
			)
		),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(PlannedMaintenanceTemplatesIndexPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
